import { useCallback, useState } from 'react';
import { generatePath, useNavigate, useParams } from 'react-router';
import { Center, Column } from '@tempmee/react';
import { CenteredSpinner } from '~/commonComponents';
import { WaitBeforeYouGoPopup } from '~/commonComponents/popups/WaitBeforeYouGoPopup';
import { useGetJobPostDetail } from '~/domain/PermanentJobs';
import { useNavigationDetector, useUserSelector } from '~/hooks';
import { IUser, ProfessionTypeEnum } from '~/models';
import { routes } from '~/routes';
import { OnsiteInterviewFormV2 } from '../components';
import { useGetUnavailableDates, useSafeLocationState, useStoreOnsiteInterview } from './hooks';
import { TCreateOnsiteInterviewPageRoutes } from './Types';
import { TJobOverviewPageRoutesLocationState } from '../JobOverview';
import { fireInterviewRequestDoLaterClicked } from '~/posthogs/events';

export const WorkDetailsPage = () => {
  const { id } = useParams<TCreateOnsiteInterviewPageRoutes>();
  const navigate = useNavigate();

  const { data, isLoading } = useGetJobPostDetail(id);
  const { isAfterJobPosting, invitedProfessionals } = useSafeLocationState();

  const currentUser: IUser = useUserSelector();

  const unavailableDates = useGetUnavailableDates(id);
  const { onSubmit } = useStoreOnsiteInterview(id);

  // The form owns its own dirty-leave popup. When the user arrives from a
  // fresh job posting (`isAfterJobPosting`), an additional popup ("Wait,
  // Before You Go") needs to fire when the user tries to leave with a clean
  // form — reminding them candidates can't apply until interviews are added.
  // The form notifies us of dirty changes; we install a blocker that only
  // fires while the form is clean, so it never overlaps with the form's own
  // Unsaved Changes blocker.
  const [isFormDirty, setIsFormDirty] = useState(false);
  const [isWaitBeforeYouGoOpen, setIsWaitBeforeYouGoOpen] = useState(false);

  const {
    allowNavigation: allowWaitNavigation,
    clearPendingNavigation: clearWaitPending,
    navigateToPending: navigateWaitPending,
  } = useNavigationDetector({
    when: isAfterJobPosting && !isFormDirty,
    onNavigationAttempt: () => setIsWaitBeforeYouGoOpen(true),
  });

  const handleDoLaterClick = useCallback(() => {
    if (!id) {
      return;
    }

    fireInterviewRequestDoLaterClicked({ postingId: id });
    navigate(generatePath(routes.jobOverview, { postingId: id, type: 'job-details' }), {
      state: {
        fromOnsiteInterviewCreation: true,
      } satisfies TJobOverviewPageRoutesLocationState,
    });
  }, [id, navigate]);

  const handleWaitConfirmLeave = useCallback(() => {
    setIsWaitBeforeYouGoOpen(false);
    allowWaitNavigation();
    navigateWaitPending();
  }, [allowWaitNavigation, navigateWaitPending]);

  const handleWaitGoBack = useCallback(() => {
    setIsWaitBeforeYouGoOpen(false);
    clearWaitPending();
  }, [clearWaitPending]);

  if (isLoading || !id) {
    return (
      <Center style={{ marginBlock: 48 }}>
        <CenteredSpinner />
      </Center>
    );
  }

  return (
    <Column width="924px" className="mx-auto">
      <OnsiteInterviewFormV2
        defaultValues={{
          breakTime: null,
          dates: [],
          hourlyRate: '',
          hoursArrival: '',
          hoursDeparture: '',
          isAssistedHygiene: currentUser.office_info.isAssistedHygiene,
          isPaidBreakTime: false,
          profession: data?.subProfession || data?.professionType || ProfessionTypeEnum.RDH,
          professionals: invitedProfessionals,
          specialty: data?.officeSpecialty || '',
        }}
        isAfterJobPosting={isAfterJobPosting}
        jobId={data?.id || id}
        jobTitle={data?.title || ''}
        office={data?.officeName || ''}
        profession={data?.subProfession || data?.professionType || ProfessionTypeEnum.RDH}
        unavailableDates={unavailableDates}
        onIsDirtyChange={isAfterJobPosting ? setIsFormDirty : undefined}
        onSubmit={onSubmit}
        {...(isAfterJobPosting
          ? {
              btnSecondaryText: "I'll Do It Later",
              btnSecondaryClick: handleDoLaterClick,
            }
          : {})}
      />

      <WaitBeforeYouGoPopup
        isOpen={isWaitBeforeYouGoOpen}
        onGoBack={handleWaitGoBack}
        onLeave={handleWaitConfirmLeave}
      />
    </Column>
  );
};
